import { useEffect, useRef, useState } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import {
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  ZoomOut,
  Loader2,
} from 'lucide-react';

pdfjsLib.GlobalWorkerOptions.workerSrc =
  'https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js';

export default function PDFViewer({ url }) {
  const canvasRef = useRef(null);

  const [pdfDoc, setPdfDoc] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalPages, setTotalPages] = useState(0);

  const [scale, setScale] = useState(0.8);

  const [pageInput, setPageInput] = useState('1');

  const [loading, setLoading] = useState(false);

  // Load PDF

  useEffect(() => {
    if (!url) return;

    const loadPDF = async () => {
      try {
        const pdf = await pdfjsLib.getDocument(url).promise;

        setPdfDoc(pdf);
        setTotalPages(pdf.numPages);

        setPageNumber(1);
        setPageInput('1');
      } catch (error) {
        console.error(error);
      }
    };

    loadPDF();
  }, [url]);

  // Render page

  useEffect(() => {
    if (!pdfDoc) return;

    const renderPage = async () => {
      setLoading(true);

      const page = await pdfDoc.getPage(pageNumber);

      const canvas = canvasRef.current;

      const context = canvas.getContext('2d');

      const containerWidth = canvas.parentElement.clientWidth - 40;

      const baseViewport = page.getViewport({
        scale: 1,
      });

      const responsiveScale = Math.min(
        containerWidth / baseViewport.width,
        scale,
      );

      const viewport = page.getViewport({
        scale: responsiveScale,
      });

      canvas.width = viewport.width;
      canvas.height = viewport.height;

      await page.render({
        canvasContext: context,
        viewport,
      }).promise;

      setLoading(false);
    };

    renderPage();
  }, [pdfDoc, pageNumber, scale]);

  const goToPage = (value) => {
    let page = Number(value);

    if (!page || page < 1 || page > totalPages) {
      return;
    }

    setPageNumber(page);
    setPageInput(String(page));
  };

  const handlePageInput = (e) => {
    if (e.key === 'Enter') {
      goToPage(pageInput);
    }
  };

  const previousPage = () => {
    setPageNumber((prev) => {
      const next = Math.max(prev - 1, 1);

      setPageInput(String(next));

      return next;
    });
  };

  const nextPage = () => {
    setPageNumber((prev) => {
      const next = Math.min(prev + 1, totalPages);

      setPageInput(String(next));

      return next;
    });
  };

  // Keyboard navigation

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'ArrowLeft') previousPage();

      if (e.key === 'ArrowRight') nextPage();
    };

    window.addEventListener('keydown', handleKey);

    return () => {
      window.removeEventListener('keydown', handleKey);
    };
  }, [totalPages]);

  return (
    <div
      className="
w-full
h-full
rounded-2xl
bg-slate-100
flex
flex-col
overflow-hidden
"
    >
      {/* TOOLBAR */}

      <div
        className="
flex
flex-wrap
items-center
justify-between
gap-3
px-4
py-3
bg-white
border-b
border-slate-200
"
      >
        {/* PAGE CONTROL */}

        <div
          className="
flex
items-center
gap-2
"
        >
          <button
            disabled={pageNumber === 1}
            onClick={previousPage}
            className="
h-9 w-9
rounded-lg
border
flex
items-center
justify-center
disabled:opacity-40
hover:bg-slate-50
"
          >
            <ChevronLeft size={18} />
          </button>

          <div
            className="
flex
items-center
rounded-lg
border
px-3
py-1
bg-slate-50
"
          >
            <input
              type="number"
              min="1"
              max={totalPages}
              value={pageInput}
              onChange={(e) => setPageInput(e.target.value)}
              onKeyDown={handlePageInput}
              className="
    bg-transparent
    outline-none
    text-center
    font-semibold
    text-sm
    appearance-none
    [&::-webkit-inner-spin-button]:appearance-none
    [&::-webkit-outer-spin-button]:appearance-none
    [&::-moz-inner-spin-button]:appearance-none
  "
            />

            <span
              className="
text-sm
text-slate-500
"
            >
              / {totalPages}
            </span>
          </div>

          <button
            disabled={pageNumber === totalPages}
            onClick={nextPage}
            className="
h-9 w-9
rounded-lg
border
flex
items-center
justify-center
disabled:opacity-40
hover:bg-slate-50
"
          >
            <ChevronRight size={18} />
          </button>
        </div>

        {/* ZOOM */}

        <div
          className="
flex
gap-2
"
        >
          <button
            onClick={() => setScale((prev) => Math.max(prev - 0.1, 0.8))}

            className="
h-9
w-9
rounded-lg
border
bg-white
flex
items-center
justify-center
hover:bg-slate-50
"
          >
            <ZoomOut size={16} />
          </button>

          <button
            onClick={() => setScale((prev) => Math.min(prev + 0.1, 2))}

            className="
h-9
w-9
rounded-lg
border
bg-white
flex
items-center
justify-center
hover:bg-slate-50
"
          >
            <ZoomIn size={16} />
          </button>
        </div>
      </div>

      {/* PDF */}

      <div
        className="
flex-1
overflow-auto
flex
justify-center
items-start
p-4
relative
"
      >
        {loading && (
          <div
            className="
absolute
top-5
right-5
bg-white
shadow-md
rounded-lg
px-3
py-2
flex
items-center
gap-2
text-sm
"
          >
            <Loader2 size={16} className="animate-spin" />
            Rendering...
          </div>
        )}

        <canvas
          ref={canvasRef}

          className="
rounded-xl
bg-white
shadow-lg
max-w-full
h-auto
"
        />
      </div>
    </div>
  );
}
